import { useEffect, useState, useCallback } from "react";
import { ClipboardList, RefreshCcw } from "lucide-react";
import { api } from "@/lib/api";

/**
 * ResetAuditLedgerStrip
 * ---------------------
 * Low-profile text stream block for the Control Deck viewport (/profile).
 * Prints the caller's destructive-reset history — timestamp · target vault
 * slice · tax year · row count — so managers can present an unalterable
 * clear trail to their CPA.
 *
 * Auto-refetches on mount, on tab-focus, and whenever any reset controller
 * dispatches the `reset-audit-log-updated` window event.
 */
export function ResetAuditLedgerStrip() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/v1/ingest/reset-audit-log?limit=50");
      setRows(Array.isArray(data?.entries) ? data.entries : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load audit ledger");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onUpdate = () => load();
    window.addEventListener("reset-audit-log-updated", onUpdate);
    window.addEventListener("focus", onUpdate);
    return () => {
      window.removeEventListener("reset-audit-log-updated", onUpdate);
      window.removeEventListener("focus", onUpdate);
    };
  }, [load]);

  const fmtStamp = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      // "[AUDIT LOG] 2026-09-08 23:39Z"
      const yyyy = d.getUTCFullYear();
      const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
      const dd = String(d.getUTCDate()).padStart(2, "0");
      const hh = String(d.getUTCHours()).padStart(2, "0");
      const mi = String(d.getUTCMinutes()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd} ${hh}:${mi}Z`;
    } catch {
      return String(iso);
    }
  };

  const actionLabel = (a) =>
    a === "wipe_slice" ? "Slice Wiped"
    : a === "wipe_all" ? "Full Wipe"
    : a === "reset_workspace" ? "Workspace Reset"
    : (a || "Reset");

  return (
    <div
      data-testid="profile-reset-audit-strip"
      className="mt-6 rounded-2xl p-4 border"
      style={{
        background: "linear-gradient(180deg, rgba(15,23,42,0.72), rgba(30,15,60,0.72))",
        border: "1px solid rgba(6,182,212,0.35)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        boxShadow: "0 0 22px rgba(6,182,212,0.18)",
      }}
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.28em] text-cyan-300">
          <ClipboardList size={12} />
          📋 System Reset Audit Ledger
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="profile-reset-audit-refresh"
          disabled={loading}
          className="text-[9px] font-mono uppercase tracking-widest text-slate-400 hover:text-cyan-200 inline-flex items-center gap-1 disabled:opacity-50"
        >
          <RefreshCcw size={10} className={loading ? "animate-spin" : ""} />
          {loading ? "Loading" : "Refresh"}
        </button>
      </div>

      {err ? (
        <div
          data-testid="profile-reset-audit-error"
          className="rounded-md px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-rose-300 border border-rose-500/40 bg-rose-500/10"
        >
          {err}
        </div>
      ) : rows.length === 0 && !loading ? (
        <div
          data-testid="profile-reset-audit-empty"
          className="rounded-md px-3 py-4 text-center text-[10px] font-mono uppercase tracking-widest text-slate-500 border border-dashed border-cyan-500/25"
        >
          No reset events on file · CPA hand-off log will populate here on the first Clear Slice
        </div>
      ) : (
        <ul
          data-testid="profile-reset-audit-list"
          className="space-y-1 max-h-[260px] overflow-y-auto pr-1"
        >
          {rows.map((r, i) => {
            const stamp = fmtStamp(r?.timestamp);
            const target = r?.target || "Unknown Slice";
            const year = r?.tax_year != null ? String(r.tax_year) : null;
            const action = actionLabel(r?.action);
            const deleted = Number(r?.deleted || 0);
            return (
              <li
                key={`${r?.timestamp || i}-${i}`}
                data-testid={`profile-reset-audit-row-${i}`}
                className="rounded-md px-3 py-1.5 text-[10.5px] font-mono flex items-center gap-2 flex-wrap"
                style={{
                  background: "rgba(15,23,42,0.55)",
                  border: "1px solid rgba(6,182,212,0.18)",
                }}
              >
                <span className="text-cyan-300/80">[AUDIT LOG]</span>
                <span className="text-slate-300">{stamp}:</span>
                <span className="text-white font-semibold">{target}</span>
                {year && (
                  <span
                    className="text-[9.5px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded"
                    style={{
                      background: "rgba(6,182,212,0.14)",
                      border: "1px solid rgba(6,182,212,0.45)",
                      color: "#67e8f9",
                    }}
                    data-testid={`profile-reset-audit-year-${i}`}
                  >
                    {year} Slice
                  </span>
                )}
                <span className="text-amber-300">{action}</span>
                <span className="text-slate-500">·</span>
                <span
                  className="text-slate-400"
                  data-testid={`profile-reset-audit-count-${i}`}
                >
                  {deleted} row{deleted === 1 ? "" : "s"} removed
                </span>
                <span className="text-slate-600 ml-auto text-[9px]">by User</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default ResetAuditLedgerStrip;
